import { useCallback } from 'react';
import { useGame } from '../contexts/GameContext';

// ─────────────────────────────────────────────────────────────
//  useSound — tiny synthesized sound kit + Leo's voice
//  All sounds generated with the Web Audio API (no files).
//  Respects the global sound toggle in GameContext.
// ─────────────────────────────────────────────────────────────

let audioCtx = null;
function getCtx() {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) audioCtx = new AC();
  if (audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
}

function tone({ freq = 440, dur = 0.15, type = 'sine', gain = 0.12, delay = 0, slide = 0 }) {
  const ctx = getCtx();
  if (!ctx) return;
  const t0 = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (slide) osc.frequency.exponentialRampToValueAtTime(Math.max(freq + slide, 40), t0 + dur);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.015);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  osc.connect(g).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

const SOUNDS = {
  // soft click for taps / navigation
  tap: () => tone({ freq: 620, dur: 0.07, type: 'sine', gain: 0.07 }),
  // page turn swoosh
  page: () => tone({ freq: 380, dur: 0.16, type: 'sine', gain: 0.06, slide: 220 }),
  // rising happy chime for correct answers
  correct: () => {
    tone({ freq: 523, dur: 0.12, type: 'triangle' });
    tone({ freq: 659, dur: 0.12, type: 'triangle', delay: 0.09 });
    tone({ freq: 784, dur: 0.22, type: 'triangle', delay: 0.18 });
  },
  // gentle "try again" (never harsh for kids)
  wrong: () => {
    tone({ freq: 330, dur: 0.16, type: 'sine', gain: 0.09 });
    tone({ freq: 262, dur: 0.22, type: 'sine', gain: 0.09, delay: 0.12 });
  },
  // sparkly star collect
  star: () => {
    tone({ freq: 880, dur: 0.09, type: 'triangle', gain: 0.1 });
    tone({ freq: 1175, dur: 0.09, type: 'triangle', gain: 0.1, delay: 0.07 });
    tone({ freq: 1568, dur: 0.18, type: 'triangle', gain: 0.1, delay: 0.14 });
  },
  // little fanfare for celebrations / badges
  celebrate: () => {
    [523, 659, 784, 1047].forEach((f, i) =>
      tone({ freq: f, dur: 0.16, type: 'square', gain: 0.05, delay: i * 0.11 })
    );
    tone({ freq: 1047, dur: 0.4, type: 'triangle', gain: 0.1, delay: 0.48 });
  },
};

// Leo speaks — shared voice helper (same warm-voice picking as BookReader)
export function speakText(text, { rate = 0.95, pitch = 1.1 } = {}) {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  const voices = window.speechSynthesis.getVoices();
  const match =
    voices.find(v => /en/i.test(v.lang) && /natural|neural/i.test(v.name)) ||
    voices.find(v => /en/i.test(v.lang) && /female|samantha|karen|zira/i.test(v.name)) ||
    voices.find(v => /en/i.test(v.lang));
  if (match) u.voice = match;
  u.rate = rate;
  u.pitch = pitch;
  window.speechSynthesis.speak(u);
}

export default function useSound() {
  const { soundOn } = useGame();

  const play = useCallback((name) => {
    if (!soundOn) return;
    const fn = SOUNDS[name];
    if (fn) { try { fn(); } catch (e) { /* audio blocked until user gesture */ } }
  }, [soundOn]);

  const say = useCallback((text, opts) => {
    if (!soundOn) return;
    try { speakText(text, opts); } catch (e) { /* no tts */ }
  }, [soundOn]);

  return { play, say, soundOn };
}
